import { Link, usePage } from "@inertiajs/react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import ChatbotWidget from "@/components/chatbot-widget";
import Sidebar from "@/components/sidebar";

const HANDLED_FLASH_KEYS_STORAGE_KEY = "obsero.handledFlashKeys";
const MAX_HANDLED_FLASH_KEYS = 50;
const AuthLayoutActionsContext = createContext(() => {});
const AuthLayoutTitleContext = createContext(() => {});

export function useAuthLayoutActions() {
  return useContext(AuthLayoutActionsContext);
}

export function useAuthLayoutTitle() {
  return useContext(AuthLayoutTitleContext);
}

function EditableHeaderTitle({ config, fallbackHeading, headingClassName }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(config.value || "");
  const inputRef = useRef(null);
  const displayedValue = config.value?.trim() || config.placeholder || fallbackHeading;

  useEffect(() => {
    if (!isEditing) setDraft(config.value || "");
  }, [config.value, isEditing]);

  useEffect(() => {
    if (!isEditing) return;

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  const finishEditing = () => {
    const nextValue = draft.trim();

    if (nextValue && nextValue !== config.value?.trim()) config.onCommit?.(nextValue);
    setIsEditing(false);
  };

  if (!config.editable) {
    return <h1 className={headingClassName}>{displayedValue}</h1>;
  }

  if (isEditing) {
    return (
      <h1 className={headingClassName}>
        <input
          ref={inputRef}
          type="text"
          className="auth-shell__heading-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={finishEditing}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              event.currentTarget.blur();
            }

            if (event.key === "Escape") {
              event.preventDefault();
              setDraft(config.value || "");
              setIsEditing(false);
            }
          }}
          aria-label={config.ariaLabel || "Redigér navn"}
          disabled={config.processing}
        />
      </h1>
    );
  }

  return (
    <h1 className={headingClassName}>
      <button
        type="button"
        className="auth-shell__heading-button"
        onClick={() => setIsEditing(true)}
        aria-label={config.ariaLabel || `Redigér navnet ${displayedValue}`}
        disabled={config.processing}
      >
        <span className="auth-shell__heading-text">{displayedValue}</span>
        <i className={`bi ${config.processing ? "bi-arrow-repeat" : "bi-pencil"} auth-shell__heading-edit-icon`} aria-hidden="true" />
      </button>
    </h1>
  );
}

function normalizeBreadcrumbs(header, fallbackEyebrow) {
  if (Array.isArray(header?.breadcrumbs) && header.breadcrumbs.length > 0) {
    return header.breadcrumbs;
  }

  if (typeof header?.breadcrumb === "string" && header.breadcrumb.trim() !== "") {
    return [{ label: header.breadcrumb }];
  }

  return [{ label: fallbackEyebrow }];
}

function readHandledFlashKeys() {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.sessionStorage.getItem(HANDLED_FLASH_KEYS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function hasHandledFlashKey(key) {
  if (!key) return false;

  return readHandledFlashKeys().includes(key);
}

function markFlashKeyAsHandled(key) {
  if (typeof window === "undefined" || !key) return;

  const nextKeys = [...readHandledFlashKeys().filter((existingKey) => existingKey !== key), key]
    .slice(-MAX_HANDLED_FLASH_KEYS);

  try {
    window.sessionStorage.setItem(HANDLED_FLASH_KEYS_STORAGE_KEY, JSON.stringify(nextKeys));
  } catch {
    // Ignore storage failures and fall back to in-memory dedupe below.
  }
}

function renderHeaderAction(action, index) {
  const {
    href,
    label,
    onClick,
    className = "",
    variant = "primary",
    icon = null,
    type = "button",
    kind = "button",
    download = false,
    disabled = false,
    ariaLabel = null
  } = action;

  const classes = kind === "status" ? className.trim() : `btn btn-${variant} ${className}`.trim();
  const content = (
    <>
      {icon ? <i className={`${icon} me-2`} aria-hidden="true" /> : null}
      <span>{label}</span>
    </>
  );

  if (kind === "status") {
    return <span key={`${label}-${index}`} className={classes} aria-live="polite">{content}</span>;
  }

  if (href) {
    if (download) {
      return (
        <a key={`${label}-${index}`} href={href} className={classes} aria-label={ariaLabel || undefined} download>
          {content}
        </a>
      );
    }

    return (
      <Link key={`${label}-${index}`} href={href} className={classes} aria-label={ariaLabel || undefined}>
        {content}
      </Link>
    );
  }

  return (
    <button
      key={`${label}-${index}`}
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || undefined}
    >
      {content}
    </button>
  );
}

export default function AuthLayout({ children, header = null }) {
  const { component, props } = usePage();
  const { flash } = props || {};
  const lastHandledFlashKeyRef = useRef(null);
  const [registeredActions, setRegisteredActions] = useState([]);
  const [registeredTitle, setRegisteredTitle] = useState(null);
  const rawPageTitle = component?.split("/").at(-1)?.replace(/[-_]/g, " ");
  const pageTitleMap = {
    new: "Ny",
    edit: "Redigér",
    show: "Vis",
    index: "Oversigt"
  };
  const pageTitle = pageTitleMap[rawPageTitle] || rawPageTitle;
  const surfaceClassName = props?.layout_surface_class_name || "workspace-shell";
  const usesFullBleedWorkspace = ["content-library-editor-surface", "organization-files-editor-surface"]
    .some((className) => surfaceClassName.split(/\s+/).includes(className));
  const fallbackEyebrow = "Kontrolpanel";
  const breadcrumbs = normalizeBreadcrumbs(header, fallbackEyebrow);
  const heading = header?.title || pageTitle || "Oversigt";
  const headingClassName = header?.title ? "auth-shell__heading mb-0" : "auth-shell__heading text-capitalize mb-0";
  const actions = Array.isArray(header?.actions) ? header.actions : [];
  const headerActions = [...actions, ...registeredActions];

  useEffect(() => {
    if (!flash.notice) return;
    if (flash.key && (lastHandledFlashKeyRef.current === flash.key || hasHandledFlashKey(flash.key))) return;

    lastHandledFlashKeyRef.current = flash.key || flash.notice;
    markFlashKeyAsHandled(flash.key);
    toast.success(flash.notice);
  }, [flash?.key, flash?.notice]);

  useEffect(() => {
    if (!flash.alert) return;
    if (flash.key && (lastHandledFlashKeyRef.current === flash.key || hasHandledFlashKey(flash.key))) return;

    lastHandledFlashKeyRef.current = flash.key || flash.alert;
    markFlashKeyAsHandled(flash.key);
    toast.error(flash.alert);
  }, [flash?.key, flash?.alert]);

  return (
    <AuthLayoutTitleContext.Provider value={setRegisteredTitle}>
      <AuthLayoutActionsContext.Provider value={setRegisteredActions}>
        <div className="auth-shell">
          <Toaster position="bottom-center" />
          <Sidebar />

          <div className="auth-shell__main">
            <header className={`auth-shell__header ${usesFullBleedWorkspace ? "auth-shell__header--full-bleed" : ""}`.trim()}>
              <div className={`auth-shell__header-inner ${usesFullBleedWorkspace ? "auth-shell__header-inner--full-bleed" : ""}`.trim()}>
                <div className="auth-shell__intro">
                  <div className="auth-shell__breadcrumbs">
                    {breadcrumbs.map((item, index) => (
                      <span key={`${item.label}-${index}`} className="auth-shell__breadcrumb-item">
                        {index > 0 ? <span className="auth-shell__breadcrumb-separator">/</span> : null}
                        {item.href ? (
                          <Link href={item.href} className="auth-shell__breadcrumb-link">
                            {item.label}
                          </Link>
                        ) : (
                          <span className="auth-shell__eyebrow mb-0">{item.label}</span>
                        )}
                      </span>
                    ))}
                  </div>
                  {registeredTitle ? (
                    <EditableHeaderTitle config={registeredTitle} fallbackHeading={heading} headingClassName={headingClassName} />
                  ) : (
                    <h1 className={headingClassName}>{heading}</h1>
                  )}
                </div>

                <div className="auth-shell__actions">
                  <button
                    type="button"
                    className="btn btn-light border d-lg-none auth-shell__mobile-toggle"
                    data-bs-toggle="offcanvas"
                    data-bs-target="#authSidebar"
                    aria-controls="authSidebar"
                    aria-label="Åbn navigation"
                  >
                    <i className="bi bi-list fs-5" aria-hidden="true" />
                  </button>

                  {headerActions.map(renderHeaderAction)}
                </div>
              </div>
            </header>

            <main className="auth-shell__content">
              <div className={`auth-shell__content-surface ${surfaceClassName}`.trim()}>
                {children}
              </div>
            </main>
          </div>
        </div>

        {props?.chatbot_available && !props?.hide_chatbot_widget ? <ChatbotWidget /> : null}
      </AuthLayoutActionsContext.Provider>
    </AuthLayoutTitleContext.Provider>
  );
}
